import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import {
    ArrowRight,
    Zap,
    CheckCircle2,
    Globe,
    Smartphone,
    ShieldCheck,
    UserCircle,
    Calculator,
    Users,
    Sparkles,
    Activity,
    Shield,
    Check,
    GraduationCap
} from 'lucide-react';

export default function LandingPage() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const { t } = useLanguage();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const solutions = [
        {
            title: 'Academic Excellence',
            desc: 'Comprehensive curriculum management, automated grading, and student performance tracking to elevate learning outcomes.',
            icon: GraduationCap,
            features: ['LMS Integration', 'Automated Grading', 'Performance Analytics']
        },
        {
            title: 'Financial Automation',
            desc: 'Streamline fee collection, payroll processing, and financial reporting with bank-grade security and accuracy.',
            icon: Calculator,
            features: ['Online Fee Payment', 'Payroll Management', 'Audit-ready Reports']
        },
        {
            title: 'Administrative Control',
            desc: 'Centralized dashboard to manage staff, students, and school resources across multiple campus locations.',
            icon: ShieldCheck,
            features: ['Multi-campus Support', 'Staff Management', 'Inventory Control']
        },
        {
            title: 'Communication Bridge',
            desc: 'Real-time notifications, instant messaging, and portal access to keep parents, teachers, and students connected.',
            icon: Smartphone,
            features: ['Push Notifications', 'Parent-Teacher Portal', 'Event Management']
        }
    ];

    const pricingPlans = [
        {
            name: 'Basic',
            price: '₹2,500',
            period: '/month',
            desc: 'Essential tools for small private schools and coaching centers.',
            features: ['Up to 150 Students', 'Student Profile Mgmt', 'Fee Collection & SMS', 'Basic Reports'],
            buttonText: 'Get Started',
            highlight: false
        },
        {
            name: 'Standard',
            price: '₹5,000',
            period: '/month',
            desc: 'Comprehensive solution for established K-12 institutions.',
            features: ['Up to 500 Students', 'Exam & Result Mgmt', 'Mobile App (Android)', 'Inventory & Library'],
            buttonText: 'Go Standard',
            highlight: true
        },
        {
            name: 'Premium',
            price: '₹12,000',
            period: '/month',
            desc: 'Full-featured ERP with advanced automation for large schools.',
            features: ['Unlimited Students', 'GPS Bus Tracking', 'HR & Payroll Suite', 'Premium Support'],
            buttonText: 'Contact Sales',
            highlight: false
        }
    ];

    const panels = [
        {
            title: 'Admin Panel',
            desc: 'Orchestrate school operations with global settings and staff audit tools.',
            color: '#3B82F6',
            icon: ShieldCheck,
            path: '/admin/dashboard'
        },
        {
            title: 'Teacher Portal',
            desc: 'Optimize classroom delivery with attendance, grading and live assignments.',
            color: '#00B884',
            icon: UserCircle,
            path: '/teacher/dashboard'
        },
        {
            title: 'Accountant',
            desc: 'Automate fee invoicing, payroll and institutional financial reporting.',
            color: '#FFFFFF',
            icon: Calculator,
            shadow: true,
            path: '#'
        },
        {
            title: 'HR Manager',
            desc: 'Manage staff life-cycles, performance metrics and leave compliance.',
            color: '#FF7020',
            icon: Users,
            path: '#'
        }
    ];

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-blue-100 selection:text-blue-600 relative overflow-x-hidden">
            {/* Background Texture */}
            <div className="fixed inset-0 pointer-events-none -z-10">
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-20"></div>
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-100/30 blur-[120px] rounded-full animate-pulse"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-100/30 blur-[120px] rounded-full"></div>
            </div>

            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-500 px-8 py-5 flex items-center justify-between ${scrolled ? 'bg-white/80 backdrop-blur-2xl border-b border-gray-100 py-4 shadow-sm' : 'bg-transparent'
                }`}>
                <div className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 bg-[#111827] rounded-xl flex items-center justify-center text-white shadow-lg shadow-gray-200 transition-transform group-hover:scale-110">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                        </svg>
                    </div>
                    <span className="text-[22px] font-black tracking-tighter text-[#111827]">School-<span className='text-blue-500'>OS</span></span>
                </div>

                <div className="hidden lg:flex items-center gap-10">
                    {[t('solutions'), t('ecosystem'), t('apps'), t('pricing')].map((item, idx) => {
                        const anchors = ['solutions', 'ecosystem', 'apps', 'pricing'];
                        return <a key={idx} href={`#${anchors[idx]}`} className="text-[13px] font-black text-gray-400 hover:text-blue-500 transition-all uppercase tracking-widest">{item}</a>;
                    })}
                    <a href="https://www.nerdos.in/get-started" target="_blank" rel="noopener noreferrer" className="text-[13px] font-black text-blue-500 hover:text-blue-600 transition-all uppercase tracking-widest">{t('enquiry')}</a>
                </div>

                <div className="flex items-center gap-4">
                    <LanguageSelector variant="landing" />
                    <button
                        onClick={() => navigate('/login')}
                        className="px-8 py-2.5 bg-[#111827] text-white rounded-xl text-[12px] font-black hover:bg-blue-600 transition-all shadow-xl shadow-gray-200 uppercase tracking-[0.2em] active:scale-95"
                    >
                        {t('signIn')}
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="pt-48 pb-32 px-8 relative">
                <div className="max-w-6xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 px-5 py-2 bg-white border border-gray-100 rounded-full text-blue-600 text-[11px] font-black uppercase tracking-[0.3em] mb-10 shadow-sm">
                        <Sparkles size={14} className="animate-spin" style={{ animationDuration: '3s' }} /> nerdOS v1.0 is here
                    </div>

                    <h1 className="text-[64px] md:text-[100px] font-black text-[#111827] leading-[0.85] tracking-tighter mb-10">
                        Institutional Intelligence. <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500">Unconditionally Fast.</span>
                    </h1>

                    <p className="max-w-3xl mx-auto text-gray-500 text-[18px] md:text-[22px] font-semibold leading-relaxed mb-16 opacity-80">
                        The definitive operating system for modern education. Orchestrate every department, module, and user from a single, high-performance console.
                    </p>

                    <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                        <a
                            href="https://www.nerdos.in/get-started"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full md:w-auto px-12 py-6 bg-blue-600 text-white rounded-[32px] text-[16px] font-black shadow-[0_20px_40px_rgba(59,130,246,0.3)] hover:bg-[#111827] hover:shadow-gray-200 hover:-translate-y-1 transition-all flex items-center justify-center gap-4 active:scale-95 group no-underline"
                        >
                            Get Started Now
                            <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
                        </a>

                        <div className="flex -space-x-3">
                            {[1, 2, 3, 4].map(i => (
                                <img key={i} src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${i}`} className="w-12 h-12 rounded-full border-4 border-white shadow-sm" alt="User" />
                            ))}
                            <div className="w-12 h-12 rounded-full border-4 border-white bg-gray-50 flex items-center justify-center text-[12px] font-black text-gray-400">+2k</div>
                        </div>
                    </div>
                </div>

                {/* Floating Aesthetic Elements */}
                <div className="absolute left-[5%] top-[60%] w-48 p-4 bg-white rounded-3xl shadow-2xl border border-gray-50 hidden xl:block animate-pulse">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-white"><Activity size={16} /></div>
                        <span className="text-[12px] font-black uppercase tracking-tight">System Status</span>
                    </div>
                    <div className="h-1.5 w-full bg-emerald-100 rounded-full overflow-hidden">
                        <div className="h-full w-[98%] bg-emerald-500"></div>
                    </div>
                    <p className="text-[10px] font-bold text-gray-400 mt-2 uppercase">99.9% Uptime</p>
                </div>
            </header>

            {/* Performance Bar (Simplified) */}
            <div className="py-20 bg-[#111827] relative z-0 overflow-hidden">
                <div className="flex flex-wrap justify-center gap-16 md:gap-32 max-w-7xl mx-auto px-8 py-10">
                    <StatItem value="98%" label="User Satisfaction" />
                    <StatItem value="1.2s" label="Avg Load Time" />
                    <StatItem value="4.9/5" label="AppStore Rating" />
                    <StatItem value="24/7" label="Global Support" />
                </div>
            </div>

            {/* Solutions Section */}
            <section id="solutions" className="py-40 px-8 relative overflow-hidden bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-24 flex flex-col items-center text-center">
                        <span className="px-5 py-1.5 bg-blue-50 text-blue-600 text-[10px] font-black rounded-full uppercase tracking-[0.3em] mb-6">End-to-End Excellence</span>
                        <h2 className="text-[48px] md:text-[64px] font-black text-[#111827] leading-[1] tracking-tighter uppercase mb-6">Enterprise Solutions.</h2>
                        <p className="text-gray-400 text-[18px] font-bold max-w-2xl">A unified platform to manage every aspect of your educational ecosystem.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {solutions.map((s, idx) => (
                            <div key={idx} className="group p-10 rounded-[48px] border border-gray-100 bg-white hover:border-blue-500 hover:shadow-2xl transition-all duration-500 text-left">
                                <div className="flex items-start gap-8">
                                    <div className="w-16 h-16 rounded-[24px] bg-blue-50 text-blue-500 flex items-center justify-center shrink-0">
                                        <s.icon size={32} />
                                    </div>
                                    <div>
                                        <h3 className="text-[24px] font-black text-[#111827] mb-4 uppercase tracking-tighter">{s.title}</h3>
                                        <p className="text-gray-400 text-[16px] font-bold mb-8 leading-relaxed">{s.desc}</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {s.features.map(f => (
                                                <div key={f} className="flex items-center gap-2 text-[13px] font-black text-gray-500 uppercase tracking-tight">
                                                    <Check size={16} className="text-emerald-500" />
                                                    {f}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Ecosystem / Panels Section */}
            <section id="ecosystem" className="py-40 px-8 relative overflow-hidden bg-white/50">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-24 flex flex-col items-center text-center">
                        <span className="px-5 py-1.5 bg-blue-50 text-blue-600 text-[10px] font-black rounded-full uppercase tracking-[0.3em] mb-6">Omni-Channel Control</span>
                        <h2 className="text-[48px] md:text-[64px] font-black text-[#111827] leading-[1] tracking-tighter uppercase mb-6">Designed For Every Role.</h2>
                        <p className="text-gray-400 text-[18px] font-bold max-w-2xl">A vertical solution that bridge the gap between administration, academia, and finance.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
                        {panels.map((p, idx) => (
                            <div
                                key={idx}
                                onClick={() => p.path !== '#' && navigate(p.path)}
                                className="group relative p-12 rounded-[64px] border border-gray-100 bg-white hover:border-blue-500 hover:shadow-[0_40px_80px_rgba(0,0,0,0.06)] transition-all duration-700 cursor-pointer flex flex-col items-start overflow-hidden hover:-translate-y-2"
                            >
                                <div className="absolute top-0 right-0 w-24 h-24 bg-slate-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-bl-[100px]"></div>

                                <div className={`w-20 h-20 rounded-[32px] mb-12 transition-all duration-700 group-hover:scale-110 group-hover:rotate-6 flex items-center justify-center border border-gray-50 shadow-xl`}
                                    style={{ backgroundColor: p.color }}>
                                    <p.icon size={28} className={p.title === 'Accountant' ? 'text-gray-400' : 'text-white'} />
                                </div>

                                <h4 className="text-[22px] font-black text-[#111827] mb-5 uppercase tracking-tighter leading-none">{p.title}</h4>
                                <p className="text-[14px] font-bold text-gray-400 leading-relaxed min-h-[72px] mb-14 transition-colors group-hover:text-gray-600">{p.desc}</p>

                                <div className="mt-auto flex items-center gap-3 text-[11px] font-black text-blue-600 uppercase tracking-[0.2em] transform translate-y-8 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                                    Launch Instance <ArrowRight size={16} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Platform Integration Section */}
            <section id="apps" className="py-40 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-8 flex flex-col lg:flex-row items-center gap-24 text-left">
                    <div className="flex-1">
                        <h2 className="text-[64px] font-black text-[#111827] leading-[0.9] tracking-tighter mb-10 uppercase text-left">
                            Modernity <br /> <span className="text-blue-600">Built-In.</span>
                        </h2>
                        <div className="space-y-12">
                            <FeatureItem
                                icon={Smartphone}
                                title="Mobile Native Experience"
                                desc="Dedicated iOS and Android apps for parents and teachers to stay informed on the move."
                            />
                            <FeatureItem
                                icon={Globe}
                                title="Global Cloud Backbone"
                                desc="High-speed data distribution with zero-latency synchronization across multiple campuses."
                            />
                            <FeatureItem
                                icon={Shield}
                                title="Bank-Grade Security"
                                desc="End-to-end encryption for all sensitive institutional data and student records."
                            />
                        </div>
                    </div>

                    <div className="flex-1 relative">
                        <div className="w-full aspect-square bg-[#111827] rounded-[80px] p-12 shadow-2xl overflow-hidden relative group">
                            <div className="flex items-center gap-4 mb-20 pointer-events-none">
                                <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                            </div>
                            <div className="space-y-6">
                                <div className="h-4 w-3/4 bg-blue-500/20 rounded-full"></div>
                                <div className="h-4 w-1/2 bg-white/5 rounded-full"></div>
                                <div className="h-32 w-full bg-white/5 rounded-3xl border border-white/10 flex items-center justify-center">
                                    <Activity size={48} className="text-blue-500/20" />
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="h-20 bg-emerald-500/10 rounded-2xl border border-emerald-500/20"></div>
                                    <div className="h-20 bg-blue-500/10 rounded-2xl border border-blue-500/20"></div>
                                    <div className="h-20 bg-white/5 rounded-2xl border border-white/10"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-40 bg-[#F8FAFC] relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-8">
                    <div className="mb-24 flex flex-col items-center text-center">
                        <span className="px-5 py-1.5 bg-blue-50 text-blue-600 text-[10px] font-black rounded-full uppercase tracking-[0.3em] mb-6">Scalable Pricing</span>
                        <h2 className="text-[48px] md:text-[64px] font-black text-[#111827] leading-[1] tracking-tighter uppercase mb-6">Growth-Ready Plans.</h2>
                        <p className="text-gray-400 text-[18px] font-bold max-w-2xl">Transparent, flexible pricing model designed to scale with your institution.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {pricingPlans.map((plan, idx) => (
                            <div key={idx} className={`relative p-12 rounded-[56px] border ${plan.highlight ? 'bg-[#111827] text-white border-[#111827] shadow-2xl scale-105 z-10' : 'bg-white border-gray-100 text-[#111827]'} flex flex-col transition-all duration-500`}>
                                {plan.highlight && (
                                    <div className="absolute top-8 right-12 px-4 py-1.5 bg-blue-600 text-white text-[10px] font-black rounded-full uppercase tracking-widest">
                                        Popular
                                    </div>
                                )}
                                <h3 className={`text-[12px] font-black uppercase tracking-[0.3em] mb-6 ${plan.highlight ? 'text-blue-400' : 'text-gray-400'}`}>{plan.name}</h3>
                                <div className="flex items-baseline gap-1 mb-8">
                                    <span className="text-[48px] font-black tracking-tighter">{plan.price}</span>
                                    <span className={`text-[14px] font-bold ${plan.highlight ? 'text-gray-400' : 'text-gray-400'}`}>{plan.period}</span>
                                </div>
                                <p className={`text-[16px] font-bold mb-10 leading-relaxed ${plan.highlight ? 'text-gray-400' : 'text-gray-400'}`}>{plan.desc}</p>

                                <div className="space-y-4 mb-12 flex-1">
                                    {plan.features.map(f => (
                                        <div key={f} className="flex items-center gap-3 text-[14px] font-bold">
                                            <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${plan.highlight ? 'bg-blue-600' : 'bg-emerald-500'}`}>
                                                <Check size={12} className="text-white" />
                                            </div>
                                            <span className={plan.highlight ? 'text-gray-300' : 'text-gray-600'}>{f}</span>
                                        </div>
                                    ))}
                                </div>

                                <a
                                    href="https://www.nerdos.in/get-started"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`w-full py-5 rounded-[24px] text-[14px] font-black uppercase tracking-[0.2em] transition-all active:scale-95 flex items-center justify-center no-underline ${plan.highlight
                                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-xl shadow-blue-900/40'
                                        : 'bg-[#111827] text-white hover:bg-blue-600 shadow-xl shadow-gray-200'
                                        }`}>
                                    {plan.buttonText}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-32 px-8 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-20">
                    <div className="flex-1 text-left">
                        <div className="flex items-center gap-3 mb-10">
                            <div className="w-10 h-10 bg-[#111827] rounded-xl flex items-center justify-center text-white">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                                </svg>
                            </div>
                            <span className="text-[24px] font-black tracking-tighter text-[#111827]">School-<span className='text-blue-500'>OS</span></span>
                        </div>
                        <p className="max-w-xs text-[15px] font-bold text-gray-400 italic">Built with performance in mind. Scaled for every institution's unique culture.</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-16 flex-1 text-left">
                        <FooterCol title="Ecosystem" links={['Admin Panel', 'Teacher Portal', 'Student Hub', 'Fee Gateway']} />
                        <FooterCol title="Platform" links={['Security', 'Infrastructure', 'Uptime', 'API Docs']} />
                        <FooterCol title="Legal" links={['Privacy', 'TOS', 'Compliance']} />
                    </div>
                </div>
                <div className="max-w-7xl mx-auto pt-20 mt-20 border-t border-gray-50 flex flex-col md:flex-row justify-between items-center gap-8">
                    <p className="text-[13px] font-black text-[#111827] uppercase tracking-[0.2em]">Product of nerdOS</p>
                    <p className="text-gray-300 text-[12px] font-semibold">© 2026 nerdOS Technologies. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}

function StatItem({ value, label }) {
    return (
        <div className="text-center">
            <h4 className="text-[48px] font-black text-white tracking-tighter leading-none mb-3">{value}</h4>
            <p className="text-blue-100/40 text-[12px] font-black uppercase tracking-[0.3em]">{label}</p>
        </div>
    );
}

function FeatureItem({ icon: Icon, title, desc }) {
    return (
        <div className="flex items-start gap-6 group text-left">
            <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-gray-400 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm shrink-0">
                <Icon size={24} />
            </div>
            <div>
                <h3 className="text-[18px] font-black text-[#111827] uppercase tracking-tight mb-2 group-hover:text-blue-600 transition-colors leading-none">{title}</h3>
                <p className="text-[14px] font-bold text-gray-400 max-w-sm tracking-tight">{desc}</p>
            </div>
        </div>
    );
}

function FooterCol({ title, links }) {
    return (
        <div className="space-y-6">
            <h4 className="text-[12px] font-black text-[#111827] uppercase tracking-[0.3em]">{title}</h4>
            <ul className="space-y-4">
                {links.map(link => (
                    <li key={link} className="text-[13px] font-bold text-gray-400 hover:text-blue-600 cursor-pointer transition-colors">{link}</li>
                ))}
            </ul>
        </div>
    );
}
